import { colors, theme } from '@/theme'
import { TEMPLATE_VARIABLES, VARIABLE_TOKEN_PATTERN, type TemplateVariable } from '@/types/template-variables'
import { Box, MenuItem, MenuList, Paper, Typography } from '@mui/material'
import { mergeAttributes, Node } from '@tiptap/core'
import { NodeViewWrapper, ReactNodeViewRenderer, ReactRenderer, type NodeViewProps } from '@tiptap/react'
import { PluginKey } from '@tiptap/pm/state'
import Suggestion, { type SuggestionOptions } from '@tiptap/suggestion'
import { forwardRef, useEffect, useImperativeHandle, useState } from 'react'

// ============================================================================
// Variable node — an atomic, non-editable token rendered as `{{key}}`.
// Content is hydrated/dehydrated at the HTML-string boundary so persisted
// preset content stays byte-compatible with the plain-text `{{key}}` format
// that interpolateTemplate() already expects.
// ============================================================================

// Both callers below only ever pass the `content` prop, which is always plain
// `{{key}}` text (sourced from the DB, or just dehydrated by dehydrateVariables) —
// so there's never an already-wrapped span here to worry about.
/** Wraps bare `{{key}}` text in a parseable span so Tiptap loads it as a variable node. */
export function hydrateVariables(html: string): string {
  return html.replace(
    VARIABLE_TOKEN_PATTERN,
    match => `<span data-variable-key="${match.slice(2, -2)}">${match}</span>`
  )
}

/** Strips the variable node's wrapper span back down to plain `{{key}}` text for persistence. */
export function dehydrateVariables(html: string): string {
  return html.replace(/<span[^>]*data-variable-key="(\w+)"[^>]*>\{\{\1\}\}<\/span>/g, '{{$1}}')
}

function filterVariables(query: string): TemplateVariable[] {
  const q = query.toLowerCase()
  return TEMPLATE_VARIABLES.filter(v => v.label.toLowerCase().includes(q) || v.key.toLowerCase().includes(q)).slice(
    0,
    30
  )
}

interface VariableListProps {
  items: TemplateVariable[]
  command: (item: TemplateVariable) => void
}

interface VariableListHandle {
  onKeyDown: (props: { event: KeyboardEvent }) => boolean
}

const VariableList = forwardRef<VariableListHandle, VariableListProps>(({ items, command }, ref) => {
  const [selectedIndex, setSelectedIndex] = useState(0)

  useEffect(() => {
    if (items) setSelectedIndex(0)
  }, [items])

  useImperativeHandle(ref, () => ({
    onKeyDown: ({ event }) => {
      if (event.key === 'ArrowUp') {
        setSelectedIndex((selectedIndex + items.length - 1) % items.length)
        return true
      }
      if (event.key === 'ArrowDown') {
        setSelectedIndex((selectedIndex + 1) % items.length)
        return true
      }
      if (event.key === 'Enter') {
        if (items[selectedIndex]) command(items[selectedIndex])
        return true
      }
      return false
    }
  }))

  return (
    <Paper
      elevation={4}
      sx={{
        minWidth: 240,
        maxHeight: 320,
        overflowY: 'auto',
        py: 0.5,
        '& .MuiMenuItem-root': { fontSize: 13, py: 0.75 }
      }}
    >
      {items.length === 0 ? (
        <Typography sx={{ px: 2, py: 1, fontSize: 13, color: colors.text.muted }}>No matching variables</Typography>
      ) : (
        <MenuList dense>
          {items.map((item, index) => (
            <MenuItem key={item.key} selected={index === selectedIndex} onClick={() => command(item)}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                <Typography sx={{ fontSize: 13 }}>{item.label}</Typography>
                <Typography sx={{ fontSize: 11, color: colors.text.muted, ml: 'auto' }}>{`{{${item.key}}}`}</Typography>
              </Box>
            </MenuItem>
          ))}
        </MenuList>
      )}
    </Paper>
  )
})
VariableList.displayName = 'VariableList'

const variableSuggestionPluginKey = new PluginKey('variableSuggestion')

const variableSuggestion: Omit<SuggestionOptions<TemplateVariable>, 'editor'> = {
  pluginKey: variableSuggestionPluginKey,
  char: '{{',
  allowSpaces: true,
  items: ({ query }) => filterVariables(query),
  command: ({ editor, range, props }) => {
    // Deferred: this fires from a MenuItem's onClick (a React event handler).
    // Inserting the node synchronously creates its NodeView's ReactRenderer,
    // which calls flushSync() internally — colliding with React's own
    // in-progress render of this click. Running on the next tick avoids it.
    setTimeout(() => {
      editor
        .chain()
        .focus()
        .insertContentAt(range, [
          { type: 'variable', attrs: { key: props.key } },
          { type: 'text', text: ' ' }
        ])
        .run()
    })
  },
  render: () => {
    let component: ReactRenderer<VariableListHandle, VariableListProps> | null = null
    let unmount: (() => void) | undefined

    return {
      onStart: props => {
        component = new ReactRenderer(VariableList, {
          props: { items: props.items, command: props.command },
          editor: props.editor
        })
        const element = component.element as HTMLElement
        // Above any MUI Dialog — the popup mounts to document.body with no
        // stacking context of its own otherwise.
        element.style.zIndex = String(theme.zIndex.modal + 1)
        unmount = props.mount(element)
      },
      onUpdate: props => {
        component?.updateProps({ items: props.items, command: props.command })
      },
      onKeyDown: props => {
        if (props.event.key === 'Escape') {
          unmount?.()
          return true
        }
        return component?.ref?.onKeyDown(props) ?? false
      },
      onExit: () => {
        unmount?.()
        component?.destroy()
      }
    }
  }
}

// The NodeView controls only the live, editable rendering (shows the friendly
// label) — renderHTML/renderText below stay `{{key}}` so getHTML()/copy-paste
// output remains byte-compatible with interpolateTemplate()'s stored format.
function VariableChip({ node }: NodeViewProps) {
  const variable = TEMPLATE_VARIABLES.find(v => v.key === node.attrs.key)
  const label = variable ? `${variable.group}: ${variable.label}` : node.attrs.key
  return (
    <NodeViewWrapper as='span' className='template-variable' contentEditable={false}>
      {label}
    </NodeViewWrapper>
  )
}

export const VariableNode = Node.create({
  name: 'variable',
  group: 'inline',
  inline: true,
  atom: true,
  selectable: true,

  addAttributes() {
    return {
      key: {
        default: null,
        parseHTML: element => element.getAttribute('data-variable-key'),
        renderHTML: attributes => (attributes.key ? { 'data-variable-key': attributes.key } : {})
      }
    }
  },

  parseHTML() {
    return [{ tag: 'span[data-variable-key]' }]
  },

  renderHTML({ node, HTMLAttributes }) {
    return ['span', mergeAttributes(HTMLAttributes, { class: 'template-variable' }), `{{${node.attrs.key}}}`]
  },

  renderText({ node }) {
    return `{{${node.attrs.key}}}`
  },

  addNodeView() {
    return ReactNodeViewRenderer(VariableChip)
  },

  addProseMirrorPlugins() {
    return [Suggestion({ editor: this.editor, ...variableSuggestion })]
  }
})
